"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
type Milestone = {
  number: string;
  title: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
const milestones: Milestone[] = [{
  number: "01",
  title: "Concept Ideation",
  description: "We begin in silence — sketching the silhouette, defining the drape, and capturing the raw energy of the urban landscape before a single stitch is placed.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_concept_sketch.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_concept_sketch.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_concept_sketch.webp-wvc-sizes",
  alt: "Technical fashion sketches on a drafting table"
}, {
  number: "02",
  title: "Textile Sourcing",
  description: "Micro-fibers, organic cottons, and high-performance technical fabrics are curated into a tactile library that informs the structural integrity of every garment.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_fabric_swatches.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_fabric_swatches.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_fabric_swatches.png-wvc-sizes",
  alt: "Luxury fabric swatches and textile samples"
}, {
  number: "03",
  title: "Pattern Engineering",
  description: "Designers and master tailors gather around the cutting table. The garment emerges as a precise dialogue between human movement and architectural form.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_pattern_cutting.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_pattern_cutting.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_pattern_cutting.png-wvc-sizes",
  alt: "Hands cutting fabric patterns on a large table"
}, {
  number: "04",
  title: "Garment Assembly",
  description: "Raw materials are transformed through rigorous construction techniques — each seam reinforced to carry the memory of its origin into the final piece.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_stitching_detail.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_stitching_detail.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_stitching_detail.webp-wvc-sizes",
  alt: "Close up of high-fashion garment stitching"
}, {
  number: "05",
  title: "Quality Stewardship",
  description: "Production teams work as stewards of the design. Each phase is inspected to preserve the drape, the tension, and the spirit of the original blueprint.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_quality_check.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_quality_check.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_quality_check.png-wvc-sizes",
  alt: "Final garment inspection in a studio setting"
}, {
  number: "06",
  title: "Collection Finalization",
  description: "Our relationship continues long after the runway. We refine, adjust, and observe — guiding the garment as it settles into its life on the street.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_runway_final.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_runway_final.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/atelier_runway_final.webp-wvc-sizes",
  alt: "Model wearing a finished Studio Thorne piece"
}];
function MilestoneCard({
  milestone,
  index
}: {
  milestone: Milestone;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25,
    rootMargin: "0px 0px -80px 0px"
  });
  const isOdd = index % 2 === 0;
  return <div ref={ref} data-index={index} className="relative grid grid-cols-12 gap-x-6 lg:gap-x-8">
      {}
      <div className="hidden lg:flex absolute left-0 right-0 top-2 pointer-events-none">
        <div className="col-start-4 flex items-center justify-center w-full" style={{
        position: "absolute",
        left: "calc((100% / 12) * 3 + (100% / 12) / 2)",
        transform: "translateX(-50%)"
      }}>
          <div className={["h-3 w-3 rounded-lg border transition-all duration-700 ease-out", isVisible ? "bg-[var(--chart-2)] border-[var(--chart-2)] scale-110" : "bg-background border-[var(--chart-3)] scale-100"].join(" ")} style={{
          boxShadow: isVisible ? "0 0 0 6px hsl(190 40% 50% / 0.12)" : "none"
        }} />
        </div>
      </div>

      {}
      <div className="lg:hidden col-span-12 flex gap-4">
        <div className="flex flex-col items-center pt-2">
          <div className={["h-3 w-3 rounded-lg border transition-all duration-700 ease-out shrink-0", isVisible ? "bg-[var(--chart-2)] border-[var(--chart-2)]" : "bg-background border-[var(--chart-3)]"].join(" ")} />
        </div>
        <div className={["flex-1 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"].join(" ")}>
          <div className="font-default italic text-[var(--chart-3)] leading-none mb-3" style={{
          fontSize: "clamp(3rem, 10vw, 4rem)"
        }}>
            {milestone.number}
          </div>
          <h3 className="font-default italic text-3xl text-foreground mb-4">
            {milestone.title}
          </h3>
          <Separator className="bg-[var(--chart-3)]/40 mb-4" />
          <p className="font-default text-base text-muted-foreground leading-relaxed mb-5">
            {milestone.description}
          </p>
          <img src={milestone.image} data-wvc-srcset={milestone.srcset} data-wvc-sizes={milestone.sizes} alt={milestone.alt} loading="lazy" decoding="async" className="w-full h-56 object-cover rounded-lg shadow-md" />
        </div>
      </div>

      {}
      {isOdd ? <div className={["hidden lg:block col-start-5 col-end-12 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"].join(" ")} style={{
      transitionDelay: isVisible ? "100ms" : "0ms"
    }}>
          <div className="font-default italic text-[var(--chart-3)] leading-none mb-4" style={{
        fontSize: "clamp(3rem, 6vw, 5rem)"
      }}>
            {milestone.number}
          </div>
          <h3 className="font-default italic text-4xl xl:text-5xl text-foreground mb-5">
            {milestone.title}
          </h3>
          <Separator className="bg-[var(--chart-3)]/40 mb-6 max-w-xs" />
          <div className="grid grid-cols-5 gap-6 items-start">
            <p className="font-default text-lg text-muted-foreground leading-relaxed col-span-3">
              {milestone.description}
            </p>
            <img src={milestone.image} data-wvc-srcset={milestone.srcset} data-wvc-sizes={milestone.sizes} alt={milestone.alt} loading="lazy" decoding="async" className="col-span-2 w-full h-44 object-cover rounded-lg shadow-md" />
          </div>
        </div> : <div className={["hidden lg:block col-start-1 col-end-4 text-right transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"].join(" ")} style={{
      transitionDelay: isVisible ? "100ms" : "0ms"
    }}>
          <div className="font-default italic text-[var(--chart-3)] leading-none mb-4" style={{
        fontSize: "clamp(3rem, 6vw, 5rem)"
      }}>
            {milestone.number}
          </div>
          <h3 className="font-default italic text-3xl xl:text-4xl text-foreground mb-5">
            {milestone.title}
          </h3>
          <Separator className="bg-[var(--chart-3)]/40 mb-6 ml-auto max-w-xs" />
          <p className="font-default text-base text-muted-foreground leading-relaxed mb-5">
            {milestone.description}
          </p>
          <img src={milestone.image} data-wvc-srcset={milestone.srcset} data-wvc-sizes={milestone.sizes} alt={milestone.alt} loading="lazy" decoding="async" className="w-full h-40 object-cover rounded-lg shadow-md ml-auto" />
        </div>}
    </div>;
}
function PullQuote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <div ref={ref} className="grid grid-cols-12 gap-x-6 lg:gap-x-8">
      <blockquote className={["col-span-12 lg:col-start-3 lg:col-end-11 text-center transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"].join(" ")}>
        <p className="font-default italic text-foreground leading-tight" style={{
        fontSize: "clamp(2rem, 5vw, 4rem)"
      }}>
          &ldquo;A garment is not merely worn &mdash; it is engineered.&rdquo;
        </p>
      </blockquote>
    </div>;
}
function IntroBlock() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} className="grid grid-cols-12 gap-x-6 lg:gap-x-8 mb-20 lg:mb-32">
      <div className={["col-span-12 lg:col-span-8 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"].join(" ")}>
        <p className="font-default text-xs tracking-[0.2em] text-[var(--chart-2)] uppercase mb-8">
          &mdash; Our Atelier
        </p>
        <h1 className="font-default text-foreground leading-[0.95]" style={{
        fontSize: "clamp(2.5rem, 7vw, 6rem)"
      }}>
          The path to{" "}
          <span className="italic text-[var(--chart-2)]">technical couture.</span>
        </h1>
      </div>
      <div className={["col-span-12 lg:col-start-9 lg:col-end-13 mt-8 lg:mt-auto transition-all duration-1000 ease-out delay-200", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"].join(" ")}>
        <p className="font-default text-lg text-muted-foreground leading-relaxed">
          A measured journey from the first technical blueprint to the final
          runway presentation. Six considered movements, carried out in close
          dialogue with fabric, form, and function.
        </p>
      </div>
    </div>;
}
function ClosingBotanical() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <div ref={ref} className="grid grid-cols-12 gap-x-6 lg:gap-x-8 mt-20 lg:mt-32">
      <div className="col-span-12 lg:col-start-4 lg:col-end-5 flex justify-center lg:justify-start">
        <svg viewBox="0 0 80 160" fill="none" xmlns="http://www.w3.org/2000/svg" className={["w-16 h-32 transition-all duration-[1400ms] ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"].join(" ")} aria-hidden="true">
          <path d="M40 160 L40 70" stroke="var(--chart-2)" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M40 100 C 20 95, 12 80, 18 60 C 28 70, 38 85, 40 100 Z" stroke="var(--chart-2)" strokeWidth="1.5" strokeLinejoin="round" fill="none" />
          <path d="M40 85 C 60 80, 68 65, 62 45 C 52 55, 42 70, 40 85 Z" stroke="var(--chart-2)" strokeWidth="1.5" strokeLinejoin="round" fill="none" />
          <path d="M40 70 C 36 50, 38 30, 44 14 C 48 30, 46 50, 40 70 Z" stroke="var(--chart-2)" strokeWidth="1.5" strokeLinejoin="round" fill="none" />
          <circle cx="40" cy="14" r="2.5" fill="var(--chart-2)" />
        </svg>
      </div>
    </div>;
}
export default function EvolutionOfAGarment() {
  return <section data-section-id="5073" id="evolution-of-a-garment" className="relative bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12 pt-48 pb-32 lg:pt-[12rem] lg:pb-40">
        <IntroBlock />

        {}
        <div className="relative">
          {}
          <div className="hidden lg:block absolute top-0 bottom-0 w-px bg-[var(--chart-3)]/40 pointer-events-none" style={{
          left: "calc((100% / 12) * 3 + (100% / 12) / 2)",
          transform: "translateX(-50%)"
        }} aria-hidden="true" />
          {}
          <div className="lg:hidden absolute top-0 bottom-0 left-[5px] w-px bg-[var(--chart-3)]/40 pointer-events-none" aria-hidden="true" />

          <div className="flex flex-col gap-20 lg:gap-32">
            {milestones.slice(0, 3).map((m, i) => <MilestoneCard key={m.number} milestone={m} index={i} />)}

            <PullQuote />

            {milestones.slice(3).map((m, i) => <MilestoneCard key={m.number} milestone={m} index={i + 3} />)}
          </div>
        </div>

        <ClosingBotanical />
      </div>
    </section>;
}